import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { BarChart3 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useUsage } from '@/context/UsageContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { ScrollCoordinator, withScrollRestorationDisabled } from '@/utils/scrollUtils';
import { formatUsageDisplay, isUnlimited } from '@/utils/usageDisplayUtils';

interface UsageMenuItemProps {
  onItemClick?: () => void; // To close parent menu
}

const UsageMenuItem: React.FC<UsageMenuItemProps> = ({ onItemClick }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Safely get usage context - it should always be available but add fallback
  let usageContext;
  try {
    usageContext = useUsage();
  } catch (error) {
    console.error('UsageMenuItem: UsageProvider not found', error);
    return null;
  }
  
  const { usageData, loading } = usageContext;
  
  const isAnonymous = !user;
  const progressPercentage = usageData.limit ? (usageData.count / usageData.limit) * 100 : 0;

  const scrollToSubscription = async () => {
    const coordinator = ScrollCoordinator.getInstance();
    
    try {
      await coordinator.executeScroll('subscription-section', {
        behavior: 'smooth',
        block: 'start',
        retries: 5,
        retryDelay: 150,
        offset: -20 // Small offset to account for any sticky headers
      });
    } catch (error) {
      console.warn('Failed to scroll to subscription section:', error);
    }
  };

  const handleClick = async () => {
    if (onItemClick) {
      onItemClick();
    }

    if (isAnonymous) {
      navigate('/signup?from=usage');
    } else {
      const isOnAccountPage = location.pathname === '/account';
      
      if (isOnAccountPage) {
        // Already on account page, scroll immediately
        await scrollToSubscription();
      } else {
        // Navigate to account page with scroll indicator
        await withScrollRestorationDisabled(async () => {
          navigate('/account?scrollTo=subscription');
          // Allow time for navigation and component mounting
          setTimeout(scrollToSubscription, 500);
        });
      }
    }
  };

  if (loading) {
    return (
      <div className="p-2 rounded hover:bg-muted">
        <div className="animate-pulse">
          <div className="h-4 bg-muted rounded w-3/4 mb-1"></div>
          <div className="h-2 bg-muted rounded w-full"></div>
        </div>
      </div>
    );
  }

  return (
    <Button
      variant="ghost"
      className="w-full justify-start p-2 h-auto flex-col items-start gap-1.5"
      onClick={handleClick}
    >
      <div className="flex items-center gap-2 w-full min-w-0">
        <BarChart3 size={16} className="shrink-0" />
        <span className="text-sm shrink-0">Usage</span>
        <Badge 
          variant={usageData.tier === 'anonymous' ? 'secondary' : 'default'} 
          className="ml-auto text-xs shrink-0 max-w-[120px] truncate"
          title={usageData.tier}
        >
          {usageData.tier}
        </Badge>
      </div>
      
      {usageData.limit !== undefined && (
        <div className="w-full space-y-1 min-w-0">
          <div className="text-xs text-muted-foreground truncate">
            {formatUsageDisplay(usageData.count, usageData.limit)} questions
          </div>
          {!isUnlimited(usageData.limit) && (
            <Progress value={progressPercentage} className="h-1 w-full" />
          )}
        </div>
      )}
      
      {isAnonymous && (
        <div className="text-xs text-muted-foreground">
          Trial Mode
        </div>
      )}
    </Button>
  );
};

export default UsageMenuItem;